import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  keyframes,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderContext } from "../contexts/OrderContext";

function EditDesignCatalogue() {
  const navigate = useNavigate();

  // Fetching store info
  const storeID = JSON.parse(localStorage.getItem("selectedMfr")).id;
  const { storeData } = useContext(OrderContext);
  const { designs = [], karigars = [], storeInfo = [] } = storeData;

  // Getting location state data
  const location = useLocation();
  const editCatalogueObj = location.state;

  // To store image URLs
  const [imageURLs, setImageURLs] = useState([]);
  const [files, setFiles] = useState([]); // File objects only

  // Check if images are available
  const [loadingImages, setLoadingImages] = useState(true);

  // Make update catalogue button active
  const [activeUpdateButton, setActiveUpdateButton] = useState(true)

  // blob url array for images
  let IMAGE_BASE_URLS_ARRAY = [];

  // blob url for all 4 images
  if (Array.isArray(storeInfo) && storeInfo.length > 0) {
    const { url_prefix } = storeInfo[0];

    IMAGE_BASE_URLS_ARRAY = [
      // `https://kumuduorderapp.blob.core.windows.net/${url_prefix}/${editCatalogueObj.tagno}`,
      // `https://kumuduorderapp.blob.core.windows.net/${url_prefix}/${editCatalogueObj.tagno}_1`,
      // `https://kumuduorderapp.blob.core.windows.net/${url_prefix}/${editCatalogueObj.tagno}_2`,
      // `https://kumuduorderapp.blob.core.windows.net/${url_prefix}/${editCatalogueObj.tagno}_3`,
      `https://kumuduorderapp.blob.core.windows.net/${url_prefix}/${editCatalogueObj.tagno_alpha}`,
      `https://kumuduorderapp.blob.core.windows.net/${url_prefix}/${editCatalogueObj.tagno_alpha}_1`,
      `https://kumuduorderapp.blob.core.windows.net/${url_prefix}/${editCatalogueObj.tagno_alpha}_2`,
      `https://kumuduorderapp.blob.core.windows.net/${url_prefix}/${editCatalogueObj.tagno_alpha}_3`,
    ];
  } else {
    console.log("No store information found.");
  }

  // Available image extenstions
  const extensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];

  // Creating blob URLs for images
  const fetchImageWithExtension = async (imageBaseUrl) => {
    for (let ext of extensions) {
      try {
        const timestamp = Date.now(); // cache buster
        const res = await axios.get(`${imageBaseUrl + ext}?t=${timestamp}`, {
          responseType: "blob",
        });

        const blobUrl = URL.createObjectURL(res.data);
        return blobUrl;
      } catch (err) {
        // Ignore 404s for missing extensions
      }
    }
    return null;
  };

  // Fetching images as blob URLs
  useEffect(() => {
    let isMounted = true;

    const loadImages = async () => {
      const urls = await Promise.all(
        IMAGE_BASE_URLS_ARRAY.map((baseUrl) => fetchImageWithExtension(baseUrl))
      );

      const validUrls = urls.filter(Boolean);
      setImageURLs(validUrls); // For preview only

      // Convert blob to File objects for FormData
      const fetchedFiles = await Promise.all(
        validUrls.map(async (url, i) => {
          const res = await fetch(url);
          const blob = await res.blob();
          const ext = blob.type.split("/")[1] || "jpg";
          const fileName = `${editCatalogueObj.tagno_alpha}${i === 0 ? "" : `_${i}`
            }.${ext}`;
          return new File([blob], fileName, { type: blob.type });
        })
      );

      setFiles(fetchedFiles);
      setLoadingImages(false);
    };

    loadImages();

    return () => {
      isMounted = false;
      imageURLs.forEach((url) => URL.revokeObjectURL(url)); // Cleanup
    };
  }, []);

  // Design catalogue data object
  const [designCatalogueData, setdesignCatalogueData] = useState({
    tagno: editCatalogueObj.tagno,
    designNo: editCatalogueObj.tagno_alpha,
    designAlias: editCatalogueObj.certificateno,
    item: storeData?.itemtypes?.find(
      (item) => item.id === editCatalogueObj.itemtype
    ),
    design: storeData?.designs?.find(
      (design) => design.id === editCatalogueObj.design
    ),
    size: editCatalogueObj.s_size_len,
    collection: editCatalogueObj.mfn_code,
    grossWt: editCatalogueObj.gross,
    netWt: editCatalogueObj.netwt,
    noOfDays: editCatalogueObj.pcs,
    toleranceFrom: editCatalogueObj.tear_wt,
    toleranceTo: editCatalogueObj.pouch_tear_wt,
    designDate: editCatalogueObj.entrydate
      ? new Date(editCatalogueObj.entrydate).toISOString().split("T")[0]
      : "",
    karigar: karigars?.find(
      (karigar) => karigar.id === Number(editCatalogueObj.supplier)
    ),
    status:
      editCatalogueObj.status === "A"
        ? { label: "Active", value: "A" }
        : { label: "Inactive", value: "I" },
    designDescription: editCatalogueObj.note,
    store_id: storeID,
  });

  // Getting designs for a particular item in design selection dropdown
  const designsPerItem = designCatalogueData.item
    ? designs.filter(
      (design) => design.itemtype_id === designCatalogueData.item.id
    )
    : [];

  // Fields for the form on UI
  const fields = [
    // Row 1
    { label: "Design No", key: "designNo", type: "text" },
    { label: "Design Alias", key: "designAlias", type: "text" },
    {
      label: "Item",
      key: "item",
      type: "select",
      options: storeData?.itemtypes?.map((item) => item.name) || [],
    },

    // Row 2
    {
      label: "Design",
      key: "design",
      type: "select",
      options: storeData?.designs?.map((design) => design.name) || [],
    },
    { label: "Size", key: "size", type: "number", step: 1 },
    {
      label: "Collection",
      key: "collection",
      type: "text",
    },

    // Row 3
    { label: "Gross Wt", key: "grossWt", type: "number", step: 0.001 },
    { label: "Net Wt", key: "netWt", type: "number", step: 0.001 },
    { label: "No of Days", key: "noOfDays", type: "number", step: 1 },

    // Row 4
    {
      label: "Tolerance/WT From",
      key: "toleranceFrom",
      type: "number",
      step: 0.1,
    },
    {
      label: "Tolerance/WT To",
      key: "toleranceTo",
      type: "number",
      step: 0.1,
    },
    {
      label: "Design Date",
      key: "designDate",
      type: "date",
    },

    // Row 5
    {
      label: "Karigar",
      key: "karigar",
      type: "select",
      options: storeData?.karigars?.map((karigar) => karigar.name) || [],
    },
    {
      label: "Status",
      key: "status",
      type: "select",
      options: [
        { label: "Active", value: "A" },
        { label: "Inactive", value: "I" },
      ],
    },
  ];

  // Restrict decimal number inputs
  const getDecimalPlaces = (step) => {
    const stepStr = step.toString();
    return stepStr.includes(".") ? stepStr.split(".")[1].length : 0;
  };

  // Decimal change handler
  const handleDecimalChange = (key, step) => (e) => {
    const value = e.target.value;
    const decimalPlaces = getDecimalPlaces(step);

    // Allow only valid decimal format with up to N digits
    const regex = new RegExp(`^\\d*(\\.\\d{0,${decimalPlaces}})?$`);

    if (value === "" || regex.test(value)) {
      setdesignCatalogueData((prev) => ({
        ...prev,
        [key]: value,
      }));

      setActiveUpdateButton(false)
    }
  };

  // Change handler
  const handleChange = (key) => (event) => {
    const value = ["designNo", "designAlias"].includes(key)
      ? event.target.value.toUpperCase()
      : event.target.value;

    setdesignCatalogueData((prev) => ({
      ...prev,
      [key]: value,
    }));
    setActiveUpdateButton(false)
  };

  // File selection or drop handler
  const onDrop = (acceptedFiles) => {
    const imageFiles = acceptedFiles.filter((file) =>
      file.type.startsWith("image/")
    );

    setFiles((prev) => {
      const total = prev.length + imageFiles.length;
      const filesToAdd =
        total <= 4 ? imageFiles : imageFiles.slice(0, 4 - prev.length);
      return [...prev, ...filesToAdd];
    });

    const previews = imageFiles.map((file) => URL.createObjectURL(file));
    setImageURLs((prev) => {
      const total = prev.length + previews.length;
      const toAdd = total <= 4 ? previews : previews.slice(0, 4 - prev.length);
      return [...prev, ...toAdd];
    });

    setActiveUpdateButton(false)
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*, video/*,application/pdf", // Accept both images and videos
    multiple: true, // Allow multiple files
  });

  // Remove uploaded file handler
  const handleRemoveFile = (indexToRemove) => {
    setActiveUpdateButton(false)
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setImageURLs((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const isFormValid = () => {
    const requiredFields = [
      "designNo",
      "design",
      "grossWt",
      "netWt",
      "karigar",
      "status",
    ];

    if (!designCatalogueData.item) return false;

    if (!designCatalogueData.item || !designCatalogueData.design) return false;

    for (let field of requiredFields) {
      if (!designCatalogueData[field]) return false;
    }
    return true;
  };

  // Update handler
  const handleUpdate = async () => {
    try {
      const payloadData = new FormData();

      for (let key in designCatalogueData) {
        const value = designCatalogueData[key];

        if (key === "item" && value?.id) {
          payloadData.append("item", value.id);
        } else if (key === "design" && value?.id) {
          payloadData.append("design", value.id);
        } else if (key === "karigar" && value?.id) {
          payloadData.append("karigar", value.id);
        } else if (key === "status" && value?.value) {
          payloadData.append("status", value.value);
        } else if (
          key !== "item" &&
          key !== "design" &&
          key !== "karigar" &&
          key !== "status" &&
          value !== null &&
          value !== undefined
        ) {
          payloadData.append(key, value);
        }
      }

      // Append image files
      files.forEach((file, index) => {
        const imageKey = `image${index + 1}`; // Must match backend expectation
        payloadData.append(imageKey, file, file.name);
      });

      // To debug the payload
      for (let [key, value] of payloadData.entries()) {
        console.log(`${key}:`, value);
      }

      // Make the request (remove Content-Type header — let Axios handle it)
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/fetchorder/catalogueupdate`,
        // `http://192.168.0.114:3000/api/fetchorder/catalogueupdate`, //Trial API
        payloadData
      );

      // Navigate to design catalogue screen
      navigate("/createdesigncatalogue", {
        state: {
          editSuccess: true,
          editedTagNo: designCatalogueData.designNo,
        },
      });
    } catch (error) {
      if (error.response) {
        console.error(
          "API responded with:",
          error.response.status,
          error.response.data
        );
      } else if (error.request) {
        console.error("No response from server:", error.request);
      } else {
        console.error("Request setup error:", error.message);
      }
    }
  };

  // Loading animation
  const dotsAnimation = keyframes`
  0% { content: "."; }
  20% { content: ".."; }
  40% { content: "..."; }
  60% { content: "...."; }
  80% { content: "...."; }
  100% { content: "."; }
`;

  return (
    <Paper
      elevation={2}
      sx={{ p: 3, borderRadius: 2, backgroundColor: "#F7F8F9", mb: 2 }}
    >
      <Grid container spacing={3}>
        {fields
          .filter(
            (field) =>
              ![
                "toleranceFrom",
                "toleranceTo",
                "designDate",
                "karigar",
                "status",
              ].includes(field.key)
          )
          .map((field, index) => (
            <Grid item xs={12} md={4} key={index}>
              {/* For item field */}
              {field.key === "item" ? (
                <Autocomplete
                  key={
                    designCatalogueData.item
                      ? designCatalogueData.item.id
                      : "item-reset"
                  }
                  disablePortal
                  id="item-select"
                  value={designCatalogueData.item}
                  onChange={(event, newValue) =>
                    setdesignCatalogueData((prev) => ({
                      ...prev,
                      item: newValue,
                    }))
                  }
                  options={storeData?.itemtypes || []}
                  getOptionLabel={(option) => option?.name || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Item"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                />
              ) : // For design type field
                field.key === "design" ? (
                  <Autocomplete
                    key={
                      designCatalogueData.design
                        ? designCatalogueData.design.id
                        : "design-reset"
                    }
                    disablePortal
                    id="design-select"
                    value={designCatalogueData.design}
                    onChange={(event, newValue) =>
                      setdesignCatalogueData((prev) => ({
                        ...prev,
                        design: newValue,
                      }))
                    }
                    options={designsPerItem}
                    getOptionLabel={(option) => option?.name || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Design"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    disabled={!designCatalogueData.item}
                  />
                ) : // For all fields with text inputs
                  field.type === "text" ? (
                    <TextField
                      label={field.label}
                      type="text"
                      value={designCatalogueData[field.key]}
                      onChange={handleChange(field.key)}
                      fullWidth
                      size="small"
                    />
                  ) : // For all fields with number inputs
                    field.type === "number" ? (
                      <TextField
                        label={field.label}
                        type="number"
                        value={designCatalogueData[field.key]}
                        onChange={handleDecimalChange(field.key, field.step || 1)}
                        fullWidth
                        size="small"
                        inputProps={{
                          step: field.step || 1,
                          min: 0,
                        }}
                      />
                    ) : (
                      // For all fields with drop down
                      <FormControl fullWidth size="small">
                        <InputLabel>{field.label}</InputLabel>
                        <Select
                          label={field.label}
                          value={designCatalogueData[field.key]}
                          onChange={handleChange(field.key)}
                        >
                          {(field.options || []).map((opt, idx) => (
                            <MenuItem key={idx} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
            </Grid>
          ))}

        {/* Row: Tolerance From, To & Date */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* Tolerance from */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Tolerance/WT From"
                value={designCatalogueData.toleranceFrom}
                onChange={handleDecimalChange("toleranceFrom", 0.1)}
                fullWidth
                size="small"
                type="number"
                inputProps={{
                  step: "0.1",
                  min: 0,
                }}
              />
            </Grid>

            {/* Tolerance to */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Tolerance/WT To"
                value={designCatalogueData.toleranceTo}
                onChange={handleDecimalChange("toleranceTo", 0.1)}
                fullWidth
                size="small"
                type="number"
                inputProps={{
                  step: "0.1",
                  min: 0,
                }}
              />
            </Grid>

            {/* Design date */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Design Date"
                type="date"
                value={
                  designCatalogueData.designDate ||
                  new Date().toISOString().split("T")[0]
                }
                onChange={handleChange("designDate")}
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true, // Ensures the label stays above the date input
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Row: Karigar & Status */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* Karigar */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                disablePortal
                id="karigar-select"
                value={designCatalogueData.karigar || null}
                onChange={(event, newValue) =>
                  setdesignCatalogueData((prev) => {
                    if (newValue) setActiveUpdateButton(false)
                    return ({
                      ...prev,
                      karigar: newValue,
                    })
                  }
                  )
                }
                options={karigars}
                getOptionLabel={(option) => option?.name || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Karigar"
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={designCatalogueData.status?.value || ""}
                  onChange={(e) => {
                    const selected = fields
                      .find((field) => field.key === "status")
                      ?.options.find(
                        (option) => option.value === e.target.value
                      );

                    setdesignCatalogueData((prev) => ({
                      ...prev,
                      status: selected,
                    }));
                  }}
                  renderValue={(selectedValue) => {
                    const selected = fields
                      .find((field) => field.key === "status")
                      ?.options.find(
                        (option) => option.value === selectedValue
                      );
                    return selected?.label || "";
                  }}
                >
                  {fields
                    .find((field) => field.key === "status")
                    ?.options.map((opt, idx) => (
                      <MenuItem key={idx} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Empty column */}
            <Grid item xs={12} md={4} />
          </Grid>
        </Grid>

        {/* DesignDescription Field */}
        <Grid item xs={12}>
          <TextField
            label="Design Description"
            multiline
            rows={4}
            fullWidth
            value={designCatalogueData.designDescription}
            onChange={handleChange("designDescription")}
            size="small"
          />
        </Grid>

        {/* Files drop zone area and show uploaded files */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* Image uploaded area */}
            <Grid item xs={12} md={4}>
              <div
                {...getRootProps()}
                className={`dropzone ${isDragActive ? "active" : ""}`}
              >
                <input {...getInputProps()} />
                <p>
                  <span className="upload-highlight">Click here</span> to upload
                  design images
                </p>
              </div>
              <Typography variant="caption" color="textSecondary" mt={1}>
                Maximum 4 images are allowed
              </Typography>
            </Grid>

            {/* Uploaded image preview area */}
            <Grid item xs={12} md={4}>
              {/* Loading till images are fetched */}
              {loadingImages ? (
                // While loading images
                <Box
                  display="flex"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 600, color: "grey" }}
                  >
                    Hang on, loading images
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: "1.5em",
                        textAlign: "left",
                        "::after": {
                          content: '""',
                          display: "inline-block",
                          animation: `${dotsAnimation} 1s steps(3, end) infinite`,
                        },
                      }}
                    />
                  </Typography>
                </Box>
              ) : // If there is no image
                imageURLs.length === 0 ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="flex-start"
                    flexDirection="column"
                  >
                    <Typography
                      sx={{ fontSize: 16, fontWeight: 600, color: "grey" }}
                    >
                      No images found for this design.
                    </Typography>
                    <Typography
                      sx={{ fontSize: 16, fontWeight: 600, color: "grey" }}
                    >
                      Go ahead and upload images.
                    </Typography>
                  </Box>
                ) : (
                  // If there are images
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    {imageURLs.map((image, index) => (
                      <Box
                        key={index}
                        sx={{
                          position: "relative",
                          width: 120,
                          height: 120,
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: 2,
                          border: "1px solid #ccc",
                        }}
                      >
                        {/* The image */}
                        <img
                          src={image}
                          alt={`preview-${index}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onClick={() => window.open(image, "_blank")}
                        />

                        {/* The cross icon */}
                        <Button
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            minWidth: "25px",
                            backgroundColor: "black",
                            color: "#fff",
                            zIndex: 2,
                            borderRadius: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                          onClick={() => handleRemoveFile(index)}
                        >
                          <Typography sx={{ fontSize: 14 }}>X</Typography>
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
            </Grid>
            <Grid item xs={12} md={4} />
          </Grid>
        </Grid>

        {/* Update and Go Back button */}
        <Grid item xs={12} mb={10}>
          {/* Buttons container */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={2}
          >
            {/* Update button */}
            <Box display="flex" justifyContent="center">
              <Button
                disabled={!isFormValid() || activeUpdateButton}
                variant="contained"
                sx={{
                  backgroundColor: "#0D7377",
                  color: "#fff",
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 4,
                  py: 1,
                  textTransform: "none",
                }}
                onClick={handleUpdate}
              >
                Update Design
              </Button>
            </Box>

            {/* Go Back button */}
            <Box display="flex" justifyContent="center">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#0D7377",
                  color: "#fff",
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 4,
                  py: 1,
                  textTransform: "none",
                }}
                onClick={() => navigate("/createdesigncatalogue")}
              >
                Go Back
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default EditDesignCatalogue;
